import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { IoReload } from "react-icons/io5";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Pagination } from "../../../components/panel/table/Pagination";
import {
  FieldProps,
  TableContent,
} from "../../../components/panel/table/TableContent";
import { TableHeader } from "../../../components/panel/table/TableHeader";
import { Popover } from "../../../components/Popover";
import { NOIMAGE_LINK } from "../../../global/variables";
import {
  getOrderItems,
  getOrders as getOrdersApi,
  OrderFilters,
  OrderItem,
  useOrderFilters,
  useOrderItems,
} from "../../../hook/queries/useOrdersV2";
import { useOrderPages } from "../../../hook/useOrdersPage";
import { setQueryParams } from "../../../hook/useQueryParams";
import { generateXlsx } from "../../../utils/generateXlsx";
import { OrderFilter } from "./filters-orders";
import { columns as columnsOrders } from "./list-orders";

interface ListOrdersRequest {
  admin?: boolean;
  client?: boolean;
  seller?: boolean;
}

export function ListOrdersItems({ admin, client, seller }: ListOrdersRequest) {
  const history = useHistory();
  const { setPages, tab, currentPage } = useOrderPages();
  const { addToast } = useToasts();

  const columns: FieldProps<OrderItem>[] = [
    {
      field: "Imagem",
      name: "image",
      viewList: true,
      tdStyles: () => ({ padding: "0.5rem" }),
      render: (item) => (
        <img
          src={item.image}
          alt="Produto"
          style={{
            width: "8rem",
            height: "8rem",
            objectFit: "contain",
            borderRadius: "0.5rem",
          }}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null; // prevents looping
            currentTarget.src = NOIMAGE_LINK;
          }}
        />
      ),
    },

    {
      name: "brand",
      field: "Marca",
      viewList: true,
      render({ product }) {
        return product?.brand?.name;
      },
    },
    {
      field: "Produto",
      name: "product",
      viewList: true,
      render: (item) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            width: 150,
            textAlign: "start",
          }}
        >
          <strong>{item.product.description}</strong>
          <span>Referência: {item.product.reference}</span>
          <span>Grade: {item.product.grid}</span>
          <span>Origem: {item.product.origin}</span>

          {!client && (
            <>
              {item.canceled?.name && (
                <span style={{ color: "#d21e26" }}> {item.canceled?.name}</span>
              )}
              {item.rejection?.name && (
                <span style={{ color: "#9700C2" }}>
                  {" "}
                  {item.rejection?.name}
                </span>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      field: "Cliente",
      name: "client",
      viewList: true,
      tdStyles: () => ({ textAlign: "start" }),
      render({ order }) {
        return order?.client?.company_name ?? "-";
      },
    },
    {
      field: "Situação",
      name: "status",
      viewList: true,
      render: (item) => {
        return (
          <nav
            style={{
              color: item.status.color,
              fontWeight: "bold",
            }}
          >
            {item.status.description}
          </nav>
        );
      },
    },
    { field: "Qtd", name: "quantity", viewList: true },
    { field: "Valor Unit.", name: "unitValue", viewList: true },
    { field: "Desconto", name: "discountValue", viewList: true },

    { field: "Total", name: "netValue", viewList: true },
    {
      name: "type",
      field: "Tipo",
      viewList: true,
      tdStyles() {
        return { width: 80 };
      },
      render({ order }) {
        return `${order.species?.cod === 9 ? "VF" : "PE"} ${moment(
          order.invoiceDate
        ).format("MM/YYYY")}`;
      },
    },
    {
      name: "entryDate",
      field: "Digitação pedido",
      viewList: true,
      render({ order }) {
        return moment(order.entryDate).format("DD/MM/YYYY");
      },
    },

    { name: "cod", field: "CÓDIGO" },
    { name: "documentNumber", field: "Nº NOTA FISCAL" },
    { name: "codClient", field: "CÓD. CLIENTE" },
    { name: "cnpj", field: "CNPJ" },
    { name: "company_name", field: "RAZÃO SOCIAL" },
    { name: "codSeller", field: "CÓD. VENDEDOR" },
    { name: "rejection", field: "MOTIVO RECUSA" },
    { name: "canceled", field: "MOTIVO CANCELAMENTO" },
    { name: "status", field: "SITUAÇÃO" },
    {
      name: "orderPeriod",
      field: "PERÍODO DE CRIAÇÃO",
    },
    {
      name: "orderPresetDate",
      field: "DATA PREDEFINIDA",
    },
    {
      name: "orderSpecificDate",
      field: "DATA ESPECÍFICA",
    },
    {
      name: "orderStartDate",
      field: "DATA INICIAL",
    },
    {
      name: "orderEndDate",
      field: "DATA FINAL",
    },
    {
      name: "billingOrderPeriod",
      field: "PERÍODO DE FATURAMENTO",
    },
    {
      name: "billingOrderPresetDate",
      field: "DATA PREDEFINIDA",
    },
    {
      name: "billingOrderSpecificDate",
      field: "DATA ESPECÍFICA",
    },
    {
      name: "billingOrderStartDate",
      field: "DATA INICIAL",
    },
    {
      name: "billingOrderEndDate",
      field: "DATA FINAL",
    },
    {
      name: "statusItem",
      field: "SITUAÇÃO DO ITEM",
    },
  ];

  const [viewFilter, setViewFilter] = useState(false);

  const [filters, setFilters] = useState<OrderFilters>(
    currentPage?.filters ?? {
      statusItem:
        tab === "Itens cancelados"
          ? "Cancelado"
          : tab === "Itens em aberto"
          ? "Aguardando Faturamento"
          : undefined,
    }
  );

  const [page, setPage] = useState<number>(currentPage?.page ?? 1);

  const formikFilter = useFormik({
    initialValues: {
      cod: undefined,
      documentNumber: undefined,
      codClient: undefined,
      cnpj: undefined,
      company_name: undefined,
      rejection: undefined,
      canceled: undefined,
      status: undefined,
      statusItem: undefined,
      scores: undefined,
      codSeller: undefined,
      brand: undefined,
      orderPeriod: undefined,
      orderPresetDate: undefined,
      orderSpecificDate: undefined,
      orderStartDate: undefined,
      orderEndDate: undefined,
      billingOrderPeriod: undefined,
      billingOrderPresetDate: undefined,
      billingOrderSpecificDate: undefined,
      billingOrderStartDate: undefined,
      billingOrderEndDate: undefined,
    },
    onSubmit: handleSubmitFilter,
  });

  function handleSubmitFilter(filter: OrderFilters) {
    setFilters(filter);
    setPage(1);
    setViewFilter(false);
  }

  async function generateReport(type: number) {
    try {
      addToast(`Exportando dados.`, {
        appearance: "info",
        autoDismiss: true,
      });

      if (type === 1) {
        const { orders } = await getOrdersApi(
          1,
          {
            ...filters,
            statusItem:
              tab === "Itens cancelados"
                ? "Cancelado"
                : tab === "Itens em aberto"
                ? "Aguardando Faturamento"
                : undefined,
          },
          999999
        );
        if (!orders) throw new Error("Sem dados");

        if (
          admin // || userType === "sellerManager"
        ) {
          const data = orders.map((item) => ({
            "CÓD. PEDIDO": item.code,
            CLIENTE: item.client?.company_name,
            "REP.": item.seller?.cod
              ? `${item.seller?.cod} ${item.seller?.abbreviation}`
              : "-",
            MARCA: item.brand?.name,
            "STATUS DO PEDIDO": item?.status?.description,
            TIPO: item.species.cod === 9 ? "Venda Futura" : "Pronta entrega",
            "DIGITAÇÃO PEDIDO": moment(item.entryDate).format(`DD/MM/YYYY`),

            FATURAMENTO: moment(item.invoiceDate).format(
              item.species.cod === 9 ? `MM/YYYY` : `DD/MM/YYYY`
            ),

            "VALOR TOTAL": item.totalMerchandise,
          })) as any[];

          await generateXlsx<object[]>({
            filename: `Pedidos`,
            data,
          });
        } else {
          const data = orders.map((item) => ({
            "CÓD. PEDIDO": item.code,
            "Nº NOTA FISCAL": item.invoiceNumber ?? "-",
            CLIENTE: item.client?.company_name,
            "REP.": item.seller?.cod,
            MARCA: item.brand?.name,
            "STATUS DO PEDIDO": item?.status?.description,
            TIPO: item.species.cod === 9 ? "Venda Futura" : "Pronta entrega",
            "DIGITAÇÃO PEDIDO": moment(item.entryDate).format(`DD/MM/YYYY`),

            FATURAMENTO: moment(item.invoiceDate).format(
              item.species.cod === 9 ? `MM/YYYY` : `DD/MM/YYYY`
            ),

            "VALOR TOTAL": item.totalMerchandise,
          })) as any[];
          await generateXlsx<object[]>({
            filename: `Pedidos`,
            data,
          });
        }
      }

      if (type === 2) {
        const { items } = await getOrderItems(
          1,
          {
            ...filters,
            statusItem:
              tab === "Itens cancelados"
                ? "Cancelado"
                : tab === "Itens em aberto"
                ? "Aguardando Faturamento"
                : undefined,
          },
          999999
        );
        if (!items) throw new Error("Sem dados");

        let data: any[] = [];
        for (const item of items) {
          data.push({
            "CÓD. PEDIDO": item.order.code,
            "Nº NOTA FISCAL": item.order.invoiceNumber ?? "-",
            CLIENTE: item.order.client?.company_name,
            REPRESENTANTE: item.order.seller?.abbreviation,
            MARCA: item.order.brand?.name,
            "STATUS DO PEDIDO": item.order.status.description,
            TIPO:
              item.order.species.cod === 9 ? "Venda Futura" : "Pronta entrega",
            "DIGITAÇÃO PEDIDO": moment(item.order.entryDate).format(
              `DD/MM/YYYY`
            ),
            "VALOR TOTAL PEDIDO": item.order.totalInvoiceValue,
            "FATURAMENTO PEDIDO": moment(item.order.invoiceDate).format(
              item.order.species.cod === 9 ? `MM/YYYY` : `DD/MM/YYYY`
            ),
            "CÓD. PRODUTO": item.product.cod,
            REF: item.product.reference,
            SITUAÇÃO: item.status.description,
            "MOTIVO CANCELAMENTO": item.canceled?.cod
              ? `${item.canceled?.cod} ${item.canceled?.name}`
              : "-",
            "MOTIVO RECUSA": item.rejection?.cod
              ? `${item.rejection?.cod} ${item.rejection?.name}`
              : "-",
            DESCRIÇÃO: item.product.description,
            GRADE: item.product.grid,
            QUANTIDADE: item.quantity,
            DESCONTO: item.discountValue,
            "PREÇO UNITÁRIO": Number(item.netValue) / item.quantity,
            SUBTOTAL: item.netValue,
          });
        }

        await generateXlsx<object[]>({
          filename: `Itens dos pedidos`,
          data: data,
        });
      }
    } catch (error) {
      addToast(
        "Desculpe, ocorreu um erro interno, Tente novamente mais tarde",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
    }
  }

  const getOrders = useOrderItems(page, {
    ...filters,
    statusItem:
      tab === "Itens cancelados"
        ? "Cancelado"
        : tab === "Itens em aberto"
        ? "Aguardando Faturamento"
        : undefined,
  });
  const getOrderFilters = useOrderFilters({
    ...filters,
    statusItem:
      tab === "Itens cancelados"
        ? "Cancelado"
        : tab === "Itens em aberto"
        ? "Aguardando Faturamento"
        : undefined,
  });

  useEffect(() => {
    setPages({ pageKey: tab, page, filters });
    // eslint-disable-next-line
  }, [page, filters]);
  useEffect(() => {
    if (page > 0) {
      setQueryParams({
        data: {
          field: "page",
          value: String(page),
        },
        type: "set",
        history,
      });
    }
    // eslint-disable-next-line
  }, [page, history]);
  useEffect(() => {
    formikFilter.handleReset({});
    // eslint-disable-next-line
  }, [tab]);

  return (
    <>
      <OrderFilter
        columns={columnsOrders}
        filters={filters}
        setFilters={setFilters}
        formikFilter={formikFilter}
        onUpdateData={() => {}}
        setViewFilter={setViewFilter}
        viewFilter={viewFilter}
        filtersData={getOrderFilters.data}
      />

      <TableHeader title="ITENS">
        <Popover
          isVisible
          Button={
            <button style={{ background: "#1d6f42" }}>
              <RiFileExcel2Fill size={19} />
              <span>EXPORTAR</span>
            </button>
          }
        >
          <li
            onClick={() => {
              generateReport(1);
            }}
          >
            <span>Listagem de pedido</span>
          </li>
          <li
            onClick={() => {
              generateReport(2);
            }}
          >
            <span>Listagem de pedido com itens</span>
          </li>
        </Popover>

        <button onClick={() => getOrders.refetch()}>
          <IoReload size={19} />
          <span>RECARREGAR</span>
        </button>
      </TableHeader>
      <TableContent<OrderItem>
        data={
          getOrders?.data?.items?.map((item) => ({
            ...item,
            netValue: Number(item.netValue).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            }),
            discountValue: Number(item.discountValue ?? 0).toLocaleString(
              "pt-BR",
              {
                style: "currency",
                currency: "BRL",
              }
            ),
            unitValue: Number(
              item.netValue ?? 0 / item.quantity
            ).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            }),
          })) ?? []
        }
        columns={columns.filter((f) => f.viewList)}
        isLoading={getOrders.isLoading}
        onClickRow={(e) => {
          if (admin) {
            history.push(`/admin/pedidosV2/${e.orderCodeAndInitials}`);
          }
          if (client) {
            history.push(`/pedidosV2/${e.orderCodeAndInitials}`);
          }
          if (seller) {
            history.push(`/representante/pedidosV2/${e.orderCodeAndInitials}`);
          }
        }}
      />

      <Pagination
        totalCountOfRegisters={getOrders.data?.totalCount ?? 0}
        registersPerPage={getOrders.data?.registersPerPage ?? 0}
        currentPage={page}
        onPageChange={(data) => {
          setPage(data);
        }}
      />
    </>
  );
}
